/**
 * Shared radios: several aircraft on one link, split into one drone each.
 *
 * A mesh reaches the server from a browser (Web Serial), from the mesh relay
 * on an operator's laptop, or from a link the server opens itself. Each mesh
 * reports every stage of the chain from radio to drone on its own, so an empty
 * mesh says where it stopped instead of just "0 aircraft".
 */

import { api, type ApiResult } from '@/api/client';

export type ChainKey = 'port' | 'bytes' | 'frames' | 'heartbeats' | 'return';

export interface ChainStage {
  key: ChainKey;
  ok: boolean;
  detail: string;
}

export type MeshVehicleState = 'pending' | 'adopted' | 'released' | 'refused';

export interface MeshVehicle {
  sysid: number;
  state: MeshVehicleState;
  /** Why it is not flying as a drone, when it is not. */
  reason: string | null;
  droneId: string | null;
  online: boolean;
  frames: number;
  framesOut: number;
  dropped: number;
  firstSeen: number;
  lastFrameAt: number;
  lastHeartbeatAt: number | null;
  vehicleType: number | null;
  autopilot: number | null;
  /** The relay address replies go to, on a UDP mesh. */
  remote: string | null;
}

export interface Mesh {
  meshId: string;
  label: string;
  origin: 'browser' | 'relay' | 'server';
  startedBy: string;
  startedAt: number;
  rateScale: number;
  link: string;
  linkOpen: boolean;
  bytesIn: number;
  framesIn: number;
  badFrames: number;
  unclaimedFrames: number;
  framesOut: number;
  lastByteAt: number | null;
  lastFrameAt: number | null;
  error: string | null;
  listenPorts: number[];
  heartbeatHosts: string[];
  onlineCount: number;
  streamDivisor: number;
  sameSysidSuspected: boolean;
  chain: ChainStage[];
  vehicles: MeshVehicle[];
}

export interface StartMesh {
  meshId: string;
  connectionString: string;
  heartbeatHosts?: string[];
}

export async function listMeshes(): Promise<ApiResult<Mesh[]>> {
  const result = await api.get<{ meshes: Mesh[] }>('/mesh', { logged: false });
  return result.ok ? { ...result, data: result.data.meshes } : result;
}

export function startMesh(body: StartMesh): Promise<ApiResult<Mesh>> {
  return api.post<Mesh>('/mesh', body, { label: `START MESH ${body.meshId}` });
}

export function stopMesh(meshId: string): Promise<ApiResult<{ stopped: string }>> {
  return api.delete<{ stopped: string }>(`/mesh/${encodeURIComponent(meshId)}`, {
    label: `STOP MESH ${meshId}`,
  });
}

export function adoptMeshVehicle(meshId: string, sysid: number): Promise<ApiResult<Mesh>> {
  return api.post<Mesh>(
    `/mesh/${encodeURIComponent(meshId)}/vehicles/${sysid}/adopt`,
    undefined,
    { label: `ADOPT ${meshId}-${sysid}` },
  );
}

// ── Silvus ──────────────────────────────────────────────────────────────────

export type LinkQuality = 'good' | 'fair' | 'poor';

export interface SilvusLink {
  source: string;
  target: string;
  sourceLabel: string;
  targetLabel: string;
  /** dB. */
  snr: number;
  quality: LinkQuality;
  rateMbps: number | null;
  airTimePercent: number | null;
}

export interface SilvusNode {
  nodeId: string;
  label: string;
  local: boolean;
}

export interface SilvusReading {
  sourceId: string;
  network: string | null;
  localIp: string | null;
  localNode: string | null;
  nodes: SilvusNode[];
  links: SilvusLink[];
  totalRateMbps: number | null;
  totalAirTimePercent: number | null;
  receivedAt: number;
  ageS: number;
  /** The connector stopped reporting; these are its last numbers, not live. */
  stale: boolean;
}

export interface SilvusPicture {
  readings: SilvusReading[];
  /** Whether any Silvus feed secret is set on the server. */
  configured: boolean;
}

export function getSilvus(): Promise<ApiResult<SilvusPicture>> {
  return api.get<SilvusPicture>('/mesh/silvus', { logged: false });
}
